/**
 * Выравнивание светлого фона фото под #FFFFFF (загрузка и показ в витрине).
 */
const TARGET_WHITE = 255
const NEAR_WHITE_MIN = 175
const NEAR_WHITE_CHROMA = 34
const MAX_SIDE = 1600

const urlCache = new Map<string, string>()
const blobUrlsToRevoke = new Set<string>()

function shouldSnapWhite(r: number, g: number, b: number, a: number): boolean {
  if (a < 12) return true
  const lo = Math.min(r, g, b)
  const hi = Math.max(r, g, b)
  if (lo >= NEAR_WHITE_MIN && hi - lo <= NEAR_WHITE_CHROMA) return true
  const lum = 0.299 * r + 0.587 * g + 0.114 * b
  return lum >= 168 && hi - lo <= NEAR_WHITE_CHROMA + 6
}

function snapNearWhiteOnCanvas(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const imgData = ctx.getImageData(0, 0, w, h)
  const d = imgData.data
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i]
    const g = d[i + 1]
    const b = d[i + 2]
    const a = d[i + 3]
    if (shouldSnapWhite(r, g, b, a)) {
      d[i] = d[i + 1] = d[i + 2] = TARGET_WHITE
      d[i + 3] = 255
    }
  }
  ctx.putImageData(imgData, 0, 0)
}

type DrawSource = {
  width: number
  height: number
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void
  dispose?: () => void
}

function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image load failed'))
    img.src = url
  })
}

/** Без crossOrigin — иначе iOS/Telegram WebView часто не даёт читать canvas. */
async function loadDrawSource(url: string): Promise<DrawSource | null> {
  const trimmed = (url || '').trim()
  if (!trimmed) return null

  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    try {
      const img = await loadHtmlImage(trimmed)
      const w = img.naturalWidth
      const h = img.naturalHeight
      return {
        width: w,
        height: h,
        draw: (ctx, tw, th) => ctx.drawImage(img, 0, 0, tw, th),
      }
    } catch {
      return null
    }
  }

  try {
    const res = await fetch(trimmed, { credentials: 'same-origin', cache: 'default' })
    if (res.ok) {
      const blob = await res.blob()
      if (typeof createImageBitmap === 'function') {
        try {
          const bitmap = await createImageBitmap(blob)
          const w = bitmap.width
          const h = bitmap.height
          return {
            width: w,
            height: h,
            draw: (ctx, tw, th) => ctx.drawImage(bitmap, 0, 0, tw, th),
            dispose: () => bitmap.close?.(),
          }
        } catch {
          /* fallback */
        }
      }
      const objUrl = URL.createObjectURL(blob)
      try {
        const img = await loadHtmlImage(objUrl)
        const w = img.naturalWidth
        const h = img.naturalHeight
        return {
          width: w,
          height: h,
          draw: (ctx, tw, th) => ctx.drawImage(img, 0, 0, tw, th),
          dispose: () => URL.revokeObjectURL(objUrl),
        }
      } catch {
        URL.revokeObjectURL(objUrl)
      }
    }
  } catch {
    /* same-origin proxy / offline */
  }

  try {
    const img = await loadHtmlImage(trimmed)
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
    }
  } catch {
    return null
  }
}

function rasterizeToBlobUrl(source: DrawSource): string | null {
  let w = source.width
  let h = source.height
  if (!w || !h) {
    source.dispose?.()
    return null
  }
  if (Math.max(w, h) > MAX_SIDE) {
    const scale = MAX_SIDE / Math.max(w, h)
    w = Math.round(w * scale)
    h = Math.round(h * scale)
  }

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  try {
    source.draw(ctx, w, h)
    snapNearWhiteOnCanvas(ctx, w, h)
  } finally {
    source.dispose?.()
  }

  const dataUrl = canvas.toDataURL('image/jpeg', 0.93)
  return dataUrl
}

function rememberDisplayUrl(key: string, display: string): string {
  const prev = urlCache.get(key)
  if (prev && prev.startsWith('blob:') && prev !== display) {
    URL.revokeObjectURL(prev)
    blobUrlsToRevoke.delete(prev)
  }
  urlCache.set(key, display)
  return display
}

async function dataUrlToBlobUrl(dataUrl: string): Promise<string> {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)
  blobUrlsToRevoke.add(blobUrl)
  return blobUrl
}

export async function normalizeAccessoryPhotoFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file

  const objUrl = URL.createObjectURL(file)
  try {
    const source = await loadDrawSource(objUrl)
    if (!source) return file
    const dataUrl = rasterizeToBlobUrl(source)
    if (!dataUrl) return file
    const res = await fetch(dataUrl)
    const blob = await res.blob()
    const base = file.name.replace(/\.[^.]+$/, '') || 'photo'
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' })
  } finally {
    URL.revokeObjectURL(objUrl)
  }
}

/** Для сетки и модалки — единый белый слой (важно для iOS WebView). */
export async function normalizeAccessoryPhotoDisplayUrl(url: string): Promise<string> {
  const trimmed = (url || '').trim()
  if (!trimmed) return trimmed
  const cached = urlCache.get(trimmed)
  if (cached) return cached

  const source = await loadDrawSource(trimmed)
  if (!source) return trimmed

  try {
    const dataUrl = rasterizeToBlobUrl(source)
    if (!dataUrl) return trimmed
    const blobUrl = await dataUrlToBlobUrl(dataUrl)
    return rememberDisplayUrl(trimmed, blobUrl)
  } catch {
    source.dispose?.()
    return trimmed
  }
}
